import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import { Loader2 } from 'lucide-react';
import { mapApiError } from '@/lib/api/error.js';
import {
  uploadKycDocument,
  deleteKycDocument,
  isImageMime,
  KYC_QUERY_KEYS,
  type KycDocumentType,
  type KycDocument,
} from '../api.js';

interface DocumentUploadCardProps {
  type: KycDocumentType;
  /** Existing uploaded document for this type, if any. */
  document: KycDocument | null;
  /** Whether re-uploading is allowed (kyc_status is unsubmitted or rejected). */
  canEdit: boolean;
}

const MAX_SIZE_MB = 10;

export function DocumentUploadCard({ type, document, canEdit }: DocumentUploadCardProps) {
  const { t } = useTranslation('kyc');
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadKycDocument({ file, type }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KYC_QUERY_KEYS.status });
      toast.success(t('upload.success'));
      setPreview(null);
    },
    onError: (err) => {
      toast.error(mapApiError(err, t));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (docId: string) => deleteKycDocument(docId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KYC_QUERY_KEYS.status });
      setPreview(null);
    },
    onError: (err) => {
      toast.error(mapApiError(err, t));
    },
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(t('upload.maxSize', { mb: MAX_SIZE_MB }));
      return;
    }

    if (!['image/jpeg', 'image/png', 'application/pdf'].includes(file.type)) {
      toast.error(t('upload.acceptedTypes'));
      return;
    }

    if (isImageMime(file.type)) {
      const url = URL.createObjectURL(file);
      setPreview(url);
    } else {
      setPreview(null);
    }

    uploadMutation.mutate(file);
    // Reset input so the same file can be re-selected after deletion.
    e.target.value = '';
  }

  const docStatus = document?.status;
  const statusTone = docStatus === 'approved'
    ? 'success'
    : docStatus === 'rejected'
    ? 'danger'
    : 'info';

  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-raised)] p-4 flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[13px] font-bold text-[var(--text-primary)]">
            {t(`documentTypes.${type}`)}
          </p>
          {document && (
            <p className="text-[11px] text-[var(--text-muted)] mt-0.5 truncate max-w-[160px]">
              {document.filename}
            </p>
          )}
        </div>
        {document && (
          <Badge tone={statusTone}>
            {t(`docStatus.${document.status}`)}
          </Badge>
        )}
      </div>

      {/* Image preview */}
      {preview && (
        <img
          src={preview}
          alt={t(`documentTypes.${type}`)}
          className="w-full h-32 object-cover rounded-lg border border-[var(--border-subtle)]"
        />
      )}

      {/* Rejection reason */}
      {docStatus === 'rejected' && document?.rejectionReason && (
        <p className="text-[11px] text-[var(--danger)] bg-[var(--danger-bg)] rounded-lg px-3 py-2">
          {document.rejectionReason}
        </p>
      )}

      {/* Upload / re-upload / delete actions */}
      <div className="flex gap-2">
        {canEdit && (
          <>
            <Button
              type="button"
              size="sm"
              variant={document ? 'secondary' : 'primary'}
              glow={!document}
              disabled={uploadMutation.isPending}
              onClick={() => inputRef.current?.click()}
            >
              {uploadMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : document ? (
                t('upload.uploadButton') + ' (replace)'
              ) : (
                t('upload.uploadButton')
              )}
            </Button>
            {document && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate(document.id)}
              >
                Remove
              </Button>
            )}
          </>
        )}
        {!canEdit && !document && (
          <p className="text-[12px] text-[var(--text-muted)]">—</p>
        )}
      </div>

      {/* Hidden file input — capture="environment" enables camera on mobile */}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,application/pdf"
        capture="environment"
        className="sr-only"
        onChange={handleFileChange}
        aria-label={t(`documentTypes.${type}`)}
      />

      <p className="text-[11px] text-[var(--text-muted)]">
        {t('upload.acceptedTypes')} · {t('upload.maxSize', { mb: MAX_SIZE_MB })}
      </p>
    </div>
  );
}
